/**
 * Customer Portal Dashboard Page
 * Main landing page for customer self-service portal
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import CustomerPortalLayout from '@/components/customer-portal/CustomerPortalLayout';
import { customerPortalService, type PortalTicket, type KBArticle } from '@/services/api/customerPortal';
import {
  Ticket,
  BookOpen,
  Search,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  MessageSquare,
  TrendingUp,
  HelpCircle,
} from 'lucide-react';

export default function CustomerPortalDashboardPage() {
  const [recentTickets, setRecentTickets] = useState<PortalTicket[]>([]);
  const [popularArticles, setPopularArticles] = useState<KBArticle[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    openTickets: 0,
    resolvedTickets: 0,
    avgResponseTime: '< 24 hours',
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load recent tickets
      const ticketResponse = await customerPortalService.getMyTickets({ limit: 5 });
      setRecentTickets(ticketResponse.tickets);

      // Calculate stats
      const openCount = ticketResponse.tickets.filter(t =>
        ['new', 'open', 'pending', 'in_progress'].includes(t.status.toLowerCase())
      ).length;
      const resolvedCount = ticketResponse.tickets.filter(t =>
        ['resolved', 'closed'].includes(t.status.toLowerCase())
      ).length;

      setStats({
        openTickets: openCount,
        resolvedTickets: resolvedCount,
        avgResponseTime: '< 24 hours',
      });

      // Load popular articles
      const articles = await customerPortalService.getPopularArticles(5);
      setPopularArticles(articles);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/customer-portal/knowledge-base?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (['new', 'open'].includes(statusLower)) {
      return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Open</Badge>;
    }
    if (['pending', 'waiting'].includes(statusLower)) {
      return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Pending</Badge>;
    }
    if (['in_progress'].includes(statusLower)) {
      return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">In Progress</Badge>;
    }
    if (['resolved', 'closed'].includes(statusLower)) {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Resolved</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const getPriorityIndicator = (priority: string) => {
    const priorityLower = priority.toLowerCase();
    if (priorityLower === 'high' || priorityLower === 'urgent') {
      return <span className="w-2 h-2 rounded-full bg-red-500" title="High Priority" />;
    }
    if (priorityLower === 'medium') {
      return <span className="w-2 h-2 rounded-full bg-amber-500" title="Medium Priority" />;
    }
    return <span className="w-2 h-2 rounded-full bg-green-500" title="Low Priority" />;
  };

  return (
    <CustomerPortalLayout title="Dashboard">
      {/* Welcome Section with Search */}
      <Card className="mb-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0">
        <CardContent className="pt-6 pb-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold mb-2">How can we help you today?</h2>
            <p className="text-blue-100 mb-6">
              Search our knowledge base or submit a support ticket.
            </p>
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search for help articles, FAQs, or topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-24 py-6 text-gray-900 bg-white border-0 rounded-lg shadow-lg"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-v2accent hover:bg-v2accent/90"
              >
                Search
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Open Tickets</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.openTickets}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <Ticket className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Resolved</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.resolvedTickets}</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Response</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.avgResponseTime}</p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                <Clock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Link to="/customer-portal/tickets/new">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full border-2 border-dashed border-blue-200 dark:border-blue-800 hover:border-blue-400">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
                <Plus className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">New Ticket</h3>
              <p className="text-sm text-gray-500">Submit a support request</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/customer-portal/tickets">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-full mb-4">
                <Ticket className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">My Tickets</h3>
              <p className="text-sm text-gray-500">View all your tickets</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/customer-portal/knowledge-base">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                <BookOpen className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Knowledge Base</h3>
              <p className="text-sm text-gray-500">Browse help articles</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/customer-portal/profile">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-4">
                <HelpCircle className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Get Help</h3>
              <p className="text-sm text-gray-500">Contact support</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Tickets & Popular Articles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Tickets */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <Ticket className="w-5 h-5 mr-2 text-blue-600" />
                  Recent Tickets
                </CardTitle>
                <CardDescription>Your latest support requests</CardDescription>
              </div>
              <Link to="/customer-portal/tickets">
                <Button variant="ghost" size="sm">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : recentTickets.length > 0 ? (
              <div className="space-y-4">
                {recentTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/customer-portal/tickets/${ticket.id}`}
                    className="block p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getPriorityIndicator(ticket.priority)}
                        <span className="text-sm font-medium text-gray-500">#{ticket.ticketNumber}</span>
                      </div>
                      {getStatusBadge(ticket.status)}
                    </div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-1 line-clamp-1">
                      {ticket.subject}
                    </h4>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(ticket.updatedAt).toLocaleDateString()}
                      {ticket.comments.length > 0 && (
                        <>
                          <span className="mx-2">·</span>
                          <MessageSquare className="w-3 h-3 mr-1" />
                          {ticket.comments.length} {ticket.comments.length === 1 ? 'reply' : 'replies'}
                        </>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No tickets yet</p>
                <Link to="/customer-portal/tickets/new">
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Ticket
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Popular Articles */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                  Popular Articles
                </CardTitle>
                <CardDescription>Most viewed help articles</CardDescription>
              </div>
              <Link to="/customer-portal/knowledge-base">
                <Button variant="ghost" size="sm">
                  Browse All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : popularArticles.length > 0 ? (
              <div className="space-y-3">
                {popularArticles.map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/customer-portal/knowledge-base/${article.slug}`}
                    className="flex items-start p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                  >
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-500 mr-3">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 line-clamp-1">
                        {article.title}
                      </h4>
                      {article.categoryName && (
                        <p className="text-sm text-gray-500">
                          {article.categoryName}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 flex-shrink-0 ml-2" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No articles available yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </CustomerPortalLayout>
  );
}
